import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import PriceRange from "../PriceRange";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import CheckList from "../CheckList";
import Tag from "../Tag";

const initialFilters = {
  endUse: [],
  collectionName: [],
  fabtype: [],
  category: [],
  trending: [],
  classic: [],
  priceRange: [0, 0],
};

// Collection filter URL mappings
const collectionUrlMappings = {
  endUse: {
    curtain: "Curtain",
    upholstery: "Upholstery",
    multipurpose: "Multipurpose",
  },
  fabtype: {
    "plains-dimout-and-blackout": "Plains, Dimout and Blackout",
    "chenille-and-boucle": "Chenille and Boucle",
    sheers: "Sheers",
    texture: "Texture",
    "embroidery-and-prints": "Embroidery and Prints",
    plains: "Plains",
    "dimout-and-blackout": "Dimout and Blackout",
    foil: "Foil",
  },
  category: {
    "room-darkening": "Room Darkening",
    "spill-safe": "Spill Safe",
    "decorative-fabrics": "Decorative Fabrics",
    "sustainable-yarns": "Sustainable Yarns",
    all: "All",
  },
};

// Helper function to generate URL slug from filter value
const getSlugFromFilterValue = (filterType, value) => {
  // For collectionName, just convert to slug format
  if (filterType === "collectionName") {
    return value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  const mappings = collectionUrlMappings[filterType];
  if (!mappings)
    return value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  const entry = Object.entries(mappings).find(
    ([, filterValue]) => filterValue === value
  );
  return entry
    ? entry[0]
    : value
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
};

function FilterPanel({
  filterCloseHandleChange,
  classname,
  collectionData,
  applySelectedFilters,
  preSelectedFilters,
}) {
  const navigate = useNavigate();
  const [minMaxResult, setMinMaxResult] = useState({});
  const [endUseList, setEndUseList] = useState([]);
  const [collectionNameList, setCollectionNameList] = useState([]);
  const [fabTypesList, setFabTypesList] = useState([]);
  const [catTypeList, setCatTypeList] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(preSelectedFilters);
  const [isInitialized, setIsInitialized] = useState(false);

  // Use refs to prevent infinite loops
  const isUpdatingFromParent = useRef(false);
  const lastAppliedFilters = useRef(null);

  const findMinMaxPrices = useCallback((arr) => {
    return arr.reduce(
      (acc, obj) => {
        if (obj.minPrice < acc.minPrice) {
          acc.minPrice = obj.minPrice;
        }
        if (obj.maxPrice > acc.maxPrice) {
          acc.maxPrice = obj.maxPrice;
        }
        return acc;
      },
      { minPrice: Infinity, maxPrice: -Infinity }
    );
  }, []);

  const getUniqueFlatData = useCallback((arr, key) => {
    if (key === "endUse") {
      // endUse is a single value, not comma-separated
      const data = arr
        .map((obj) => obj[key])
        .filter((value) => value && value.trim() !== "");
      return [...new Set(data)];
    } else if (key === "name") {
      // Collection names are single values
      const data = arr
        .map((obj) => obj[key])
        .filter((value) => value && value.trim() !== "");
      return [...new Set(data)];
    } else {
      // For fabType and category, they can be comma-separated
      const data = arr.flatMap((obj) => {
        if (obj[key] && typeof obj[key] === "string") {
          return obj[key].split(",").map((s) => s.trim());
        }
        return [];
      });
      const filteredData = data.filter((value) => value.trim() !== "");
      return [...new Set(filteredData)];
    }
  }, []);

  useEffect(() => {
    if (collectionData.length > 0) {
      setMinMaxResult(findMinMaxPrices(collectionData));
      setEndUseList(getUniqueFlatData(collectionData, "endUse"));
      setCollectionNameList(getUniqueFlatData(collectionData, "name"));
      setFabTypesList(getUniqueFlatData(collectionData, "fabType"));
      setCatTypeList(getUniqueFlatData(collectionData, "category"));
    }
  }, [collectionData, findMinMaxPrices, getUniqueFlatData]);

  // Initialize filters only once when minMaxResult is available
  useEffect(() => {
    if (Object.keys(minMaxResult).length > 0 && !isInitialized) {
      isUpdatingFromParent.current = true;
      let tempValue = [
        preSelectedFilters["priceRange"][0] === 0
          ? 0
          : preSelectedFilters["priceRange"][0],
        preSelectedFilters["priceRange"][1] === 0
          ? minMaxResult?.maxPrice
          : preSelectedFilters["priceRange"][1],
      ];
      const initialState = {
        endUse: preSelectedFilters?.endUse || [],
        collectionName: preSelectedFilters?.collectionName || [],
        fabtype: preSelectedFilters?.fabtype || [],
        category: preSelectedFilters?.category || [],
        trending: preSelectedFilters?.trending || [],
        classic: preSelectedFilters?.classic || [],
        priceRange: tempValue,
      };
      setSelectedFilters(initialState);
      lastAppliedFilters.current = JSON.stringify(initialState);
      setIsInitialized(true);
      isUpdatingFromParent.current = false;
    }
  }, [minMaxResult, isInitialized, preSelectedFilters]);

  // Update local state when preSelectedFilters change from parent
  useEffect(() => {
    if (
      isInitialized &&
      Object.keys(minMaxResult).length > 0 &&
      !isUpdatingFromParent.current
    ) {
      const currentFiltersString = JSON.stringify(preSelectedFilters);
      if (lastAppliedFilters.current !== currentFiltersString) {
        isUpdatingFromParent.current = true;
        let tempValue = [
          preSelectedFilters["priceRange"][0] === 0
            ? 0
            : preSelectedFilters["priceRange"][0],
          preSelectedFilters["priceRange"][1] === 0
            ? minMaxResult?.maxPrice
            : preSelectedFilters["priceRange"][1],
        ];

        const newFilters = {
          endUse: preSelectedFilters?.endUse || [],
          collectionName: preSelectedFilters?.collectionName || [],
          fabtype: preSelectedFilters?.fabtype || [],
          category: preSelectedFilters?.category || [],
          trending: preSelectedFilters?.trending || [],
          classic: preSelectedFilters?.classic || [],
          priceRange: tempValue,
        };

        setSelectedFilters(newFilters);
        lastAppliedFilters.current = JSON.stringify(newFilters);
        isUpdatingFromParent.current = false;
      }
    }
  }, [preSelectedFilters, minMaxResult, isInitialized]);

  // Apply filters to parent when local filters change (but not when updating from parent)
  useEffect(() => {
    if (isInitialized && !isUpdatingFromParent.current) {
      const currentFiltersString = JSON.stringify(selectedFilters);
      if (lastAppliedFilters.current !== currentFiltersString) {
        applySelectedFilters(selectedFilters);
        lastAppliedFilters.current = currentFiltersString;
      }
    }
  }, [selectedFilters, isInitialized, applySelectedFilters]);

  const handleCheckboxChange = useCallback(
    (type, value) => {
      if (type === "priceRange") {
        let tempValue = [0, minMaxResult?.maxPrice];
        const newFilters = {
          ...selectedFilters,
          priceRange: tempValue,
        };
        setSelectedFilters(newFilters);

        // Update URL - if only price filter is active, go to base collections
        const hasOtherFilters = Object.entries(newFilters).some(
          ([key, filterValue]) => {
            if (key === "priceRange") return false;
            return Array.isArray(filterValue) && filterValue.length > 0;
          }
        );

        if (!hasOtherFilters) {
          navigate("/collections", { replace: true });
        }
      } else {
        const currentArray = selectedFilters[type] || [];
        const updatedFilters = currentArray.includes(value)
          ? currentArray.filter((item) => item !== value)
          : [...currentArray, value];

        const newFilters = {
          ...selectedFilters,
          [type]: updatedFilters,
        };

        setSelectedFilters(newFilters);

        // Count total active filters (excluding price range)
        const totalActiveFilters = Object.keys(newFilters).reduce(
          (count, filterType) => {
            if (filterType === "priceRange") return count;
            const filterArray = newFilters[filterType] || [];
            return count + filterArray.length;
          },
          0
        );

        // Generate URL based on filter count
        if (totalActiveFilters === 0) {
          // No filters active - go to default collections page
          navigate("/collections", { replace: true });
        } else if (totalActiveFilters === 1) {
          // Single filter active - use specific URL
          const activeFilterType = Object.keys(newFilters).find(
            (filterType) => {
              if (filterType === "priceRange") return false;
              const filterArray = newFilters[filterType] || [];
              return filterArray.length > 0;
            }
          );

          if (activeFilterType && newFilters[activeFilterType].length > 0) {
            const slug = getSlugFromFilterValue(
              activeFilterType,
              newFilters[activeFilterType][0]
            );
            // Use lowercase for URL but keep the original filterType structure
            const urlFilterTypeMap = {
              'collectionName': 'collectionname',
              'fabtype': 'fabtype',
              'endUse': 'enduse',
              'category': 'category',
              'trending': 'trending',
              'classic': 'classic'
            };
            const urlFilterType = urlFilterTypeMap[activeFilterType] || activeFilterType;
            navigate(`/collections/${urlFilterType}/${slug}`, {
              replace: true,
            });
          }
        } else {
          // Multiple filters active - use default collections page
          navigate("/collections", { replace: true });
        }
      }
    },
    [selectedFilters, minMaxResult?.maxPrice, navigate]
  );

  const getCheckedItems = useCallback(
    (type) => {
      const filterArray = selectedFilters[type] || [];
      return filterArray.reduce((acc, item) => {
        acc[item] = true;
        return acc;
      }, {});
    },
    [selectedFilters]
  );

  const hasAnyFilters = useMemo(() => {
    const { priceRange, ...otherFilters } = selectedFilters;
    const isPriceFilterApplied =
      priceRange[0] !== initialFilters.priceRange[0] ||
      priceRange[1] !== minMaxResult?.maxPrice;
    return (
      isPriceFilterApplied ||
      Object.values(otherFilters).some((array) => array.length > 0)
    );
  }, [selectedFilters, minMaxResult?.maxPrice]);

  const resetFilters = useCallback(() => {
    let tempValue = [0, minMaxResult?.maxPrice];
    const resetFiltersState = {
      ...initialFilters,
      priceRange: tempValue,
    };
    setSelectedFilters(resetFiltersState);

    // Navigate back to main collections page when resetting filters
    navigate("/collections", { replace: true });
  }, [minMaxResult?.maxPrice, navigate]);

  const handlePriceChange = useCallback((newPriceRange) => {
    setSelectedFilters((prevState) => ({
      ...prevState,
      priceRange: newPriceRange,
    }));
  }, []);

  return (
    <div className={`bs-filter bg-light ${classname}`}>
      <div className="bs-filter__title-wrap">
        <div className="bs-filter__title-cont">
          <button className="bs-btn bs-btn__btn-icon-link">
            <span className="bs-btn__icon icon icon-filter"></span>
            <span className="bs-btn__btn-text">filter</span>
          </button>
          <button
            className="bs-btn bs-btn__btn-icon-link bs-filter__close"
            onClick={() => filterCloseHandleChange()}
          >
            <span className="icon icon-simple-close bs-filter__icon"></span>
          </button>
        </div>
        <div className="bs-filter__top-wrap">
          <div className="bs-filter__tag-wrap">
            {Object.keys(selectedFilters).map((key) => {
              const filterArray = selectedFilters[key] || [];
              if (filterArray.length > 0) {
                return filterArray.map((item, index) => {
                  if (key !== "priceRange") {
                    return (
                      <React.Fragment key={`${key}-${index}`}>
                        <Tag
                          title={item}
                          onCheckboxChange={(value) =>
                            handleCheckboxChange(key, value)
                          }
                        />
                      </React.Fragment>
                    );
                  } else if (
                    (selectedFilters["priceRange"][0] !== 0 ||
                      selectedFilters["priceRange"][1] !==
                        minMaxResult?.maxPrice) &&
                    index === 0
                  ) {
                    return (
                      <React.Fragment key={`${key}-${index}`}>
                        <Tag
                          title={`Rs. ${selectedFilters["priceRange"][0]} - Rs. ${selectedFilters["priceRange"][1]}`}
                          onCheckboxChange={(value) =>
                            handleCheckboxChange(key, value)
                          }
                        />
                      </React.Fragment>
                    );
                  }
                  return null;
                });
              }
              return null;
            })}
          </div>
          {hasAnyFilters && (
            <button
              className="bs-btn bs-btn__btn-icon-link"
              onClick={resetFilters}
            >
              <span className="bs-btn__btn-text">Clear All</span>
            </button>
          )}
        </div>
      </div>
      <div className="bs-filter__body-wrap">
        <div className="bs-filter__list">
          <Accordion className="bs-filter__item">
            <AccordionSummary
              expandIcon={<span className="icon icon-down"></span>}
              aria-controls="panel1-content"
              id="panel1-header"
              className=""
            >
              <h4 className="bs-filter__label">PRICE</h4>
            </AccordionSummary>
            <AccordionDetails>
              {Object.keys(minMaxResult).length > 0 ? (
                <PriceRange
                  minMaxResult={minMaxResult}
                  handlePriceChange={(param) => {
                    handlePriceChange(param);
                  }}
                  priceVal={selectedFilters.priceRange}
                />
              ) : null}
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="bs-filter__list">
          <Accordion className="bs-filter__item">
            <AccordionSummary
              expandIcon={<span className="icon icon-down"></span>}
              aria-controls="panel1-content"
              id="panel1-header"
              className=""
            >
              <h4 className="bs-filter__label">COLLECTIONS</h4>
            </AccordionSummary>
            <AccordionDetails>
              <CheckList
                data={collectionNameList}
                checkedItems={getCheckedItems("collectionName")}
                onCheckboxChange={(value) =>
                  handleCheckboxChange("collectionName", value)
                }
              />
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="bs-filter__list">
          <Accordion className="bs-filter__item">
            <AccordionSummary
              expandIcon={<span className="icon icon-down"></span>}
              aria-controls="panel1-content"
              id="panel1-header"
              className=""
            >
              <h4 className="bs-filter__label">TRENDING/CLASSIC</h4>
            </AccordionSummary>
            <AccordionDetails>
              <div className="lyt-check-list__item">
                <div className="bs-checkbox">
                  <input
                    type="checkbox"
                    id="trending"
                    name="trending"
                    onChange={() =>
                      handleCheckboxChange("trending", "Trending")
                    }
                    checked={
                      selectedFilters?.trending?.includes("Trending") || false
                    }
                  />
                  <label className="bs-checkbox__label" htmlFor="trending">
                    TRENDING
                  </label>
                </div>

                <div className="bs-checkbox">
                  <input
                    type="checkbox"
                    id="classic"
                    name="classic"
                    onChange={() => handleCheckboxChange("classic", "Classic")}
                    checked={
                      selectedFilters?.classic?.includes("Classic") || false
                    }
                  />
                  <label className="bs-checkbox__label" htmlFor="classic">
                    CLASSIC
                  </label>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="bs-filter__list">
          <Accordion className="bs-filter__item">
            <AccordionSummary
              expandIcon={<span className="icon icon-down"></span>}
              aria-controls="panel1-content"
              id="panel1-header"
              className=""
            >
              <h4 className="bs-filter__label">END USE</h4>
            </AccordionSummary>
            <AccordionDetails>
              <CheckList
                data={endUseList}
                checkedItems={getCheckedItems("endUse")}
                onCheckboxChange={(value) =>
                  handleCheckboxChange("endUse", value)
                }
              />
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="bs-filter__list">
          <Accordion className="bs-filter__item">
            <AccordionSummary
              expandIcon={<span className="icon icon-down"></span>}
              aria-controls="panel1-content"
              id="panel1-header"
              className=""
            >
              <h4 className="bs-filter__label">FABRIC TYPE</h4>
            </AccordionSummary>
            <AccordionDetails>
              <CheckList
                data={fabTypesList}
                checkedItems={getCheckedItems("fabtype")}
                onCheckboxChange={(value) =>
                  handleCheckboxChange("fabtype", value)
                }
              />
            </AccordionDetails>
          </Accordion>
        </div>

        <div className="bs-filter__list">
          <Accordion className="bs-filter__item">
            <AccordionSummary
              expandIcon={<span className="icon icon-down"></span>}
              aria-controls="panel1-content"
              id="panel1-header"
              className=""
            >
              <h4 className="bs-filter__label">CATEGORY</h4>
            </AccordionSummary>
            <AccordionDetails>
              <CheckList
                data={catTypeList}
                checkedItems={getCheckedItems("category")}
                onCheckboxChange={(value) =>
                  handleCheckboxChange("category", value)
                }
              />
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
      <div className="bs-filter__bottom-wrap">
        <button
          className="bs-btn bs-btn__btn-icon-link"
          onClick={() => filterCloseHandleChange()}
        >
          <span className="bs-btn__btn-text">CLOSE</span>
        </button>
      </div>
    </div>
  );
}

export default FilterPanel;
